import { Check, Clock3, PenLine, Send, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, EmptyState, PageHeader, formatRelativeTime } from "../components/ui";
import { useAuth } from "../lib/auth";
import { useWorkspace } from "../lib/workspace";
import { apiRequest } from "../lib/api";
import type { FacebookConversation, FacebookInbox, FacebookSendResult } from "../../shared/facebook";
import type { DraftRevision } from "../../shared/types";

type BusyAction = "generate" | "human" | "approve" | "send" | null;

export function InboxPage() {
  const { data } = useWorkspace();
  const { status, accessToken } = useAuth();
  const [facebookInbox, setFacebookInbox] = useState<FacebookInbox | null>(null);
  const [facebookConversation, setFacebookConversation] = useState<FacebookConversation | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftBody, setDraftBody] = useState("");
  const [draft, setDraft] = useState<DraftRevision | null>(null);
  const [busy, setBusy] = useState<BusyAction>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const selectedConversationRef = useRef<string | null>(null);

  const loadFacebookInbox = useCallback(async (cursor?: string) => {
    if (status !== "authenticated" || !accessToken) return;
    if (cursor) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const page = await apiRequest<FacebookInbox>(`/api/facebook/inbox${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, accessToken);
      setFacebookInbox((current) => {
        if (!cursor || !current) return page;
        const seen = new Set(current.inbox.map((item) => item.id));
        return { ...page, inbox: [...current.inbox, ...page.inbox.filter((item) => !seen.has(item.id))] };
      });
    } catch (reason) {
      setError(errorText(reason));
    } finally {
      if (cursor) setLoadingMore(false); else setLoading(false);
    }
  }, [accessToken, status]);

  const loadConversation = useCallback(async (conversationId: string) => {
    if (status !== "authenticated" || !accessToken) return;
    setDetailLoading(true);
    setError(null);
    try {
      const detail = await apiRequest<FacebookConversation>(`/api/facebook/inbox/${encodeURIComponent(conversationId)}`, accessToken);
      if (selectedConversationRef.current === conversationId && detail.conversation.id === conversationId) setFacebookConversation(detail);
    } catch (reason) {
      if (selectedConversationRef.current === conversationId) setError(errorText(reason));
    } finally {
      if (selectedConversationRef.current === conversationId) setDetailLoading(false);
    }
  }, [accessToken, status]);

  useEffect(() => {
    // Fetching auth-dependent external state; the loading transition is intentional.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (status === "authenticated") void loadFacebookInbox();
  }, [loadFacebookInbox, status]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const inbox = status === "preview" ? data?.inbox ?? [] : facebookInbox?.inbox ?? [];
  const selectedSummary = inbox.find((item) => item.id === selectedId) ?? inbox[0] ?? null;
  const effectiveSelectedId = selectedSummary?.id ?? null;
  const conversation = status === "preview"
    ? data?.inbox.find((item) => item.id === effectiveSelectedId) ?? null
    : facebookConversation?.conversation.id === effectiveSelectedId ? facebookConversation.conversation : null;
  useEffect(() => {
    selectedConversationRef.current = effectiveSelectedId;
    if (status === "authenticated" && effectiveSelectedId) {
      // Load full message bodies only for the selected workspace-bound conversation.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFacebookConversation(null);
      void loadConversation(effectiveSelectedId);
    }
  }, [effectiveSelectedId, loadConversation, status]);
  const channel = data?.channels.find((item) => item.id === (conversation?.channelId ?? selectedSummary?.channelId));
  const withinWindow = conversation
    ? now - new Date(conversation.lastInboundAt).getTime() < 24 * 60 * 60 * 1000
    : false;
  const existingDraft = useMemo(
    () => {
      const availableDrafts = status === "preview" ? data?.drafts ?? [] : facebookConversation?.drafts ?? [];
      return availableDrafts.find((item) => item.targetId === effectiveSelectedId && item.kind === "message_reply") ?? null;
    },
    [data?.drafts, effectiveSelectedId, facebookConversation?.drafts, status],
  );
  const operation = draft ? facebookConversation?.operations.find((item) => item.draftId === draft.id) ?? null : null;
  const draftMatchesConversation = Boolean(draft && conversation && draft.targetId === conversation.id && draft.channelId === conversation.channelId);

  useEffect(() => {
    // Reset the editable review buffer when the selected server revision changes.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraft(existingDraft);
    setDraftBody(existingDraft?.body ?? "");
    setMessage(null);
    setError(null);
  }, [existingDraft, effectiveSelectedId]);

  if (!data) return null;

  async function createDraft(source: "ai" | "human") {
    if (!conversation || !data) return;
    const humanBody = draftBody.trim();
    if (source === "human" && !humanBody) return;
    setBusy(source === "ai" ? "generate" : "human");
    setMessage(null);
    setError(null);
    const requestedConversationId = conversation.id;
    try {
      if (status === "preview") {
        const body = source === "human"
          ? humanBody
          : data.context.faqs[0]?.answer ?? "ဒီမေးခွန်းကို စစ်ဆေးပြီး ပြန်ဖြေပေးပါမယ်။";
        const previewDraft: DraftRevision = {
          id: "preview-draft",
          kind: "message_reply",
          targetId: conversation.id,
          channelId: channel?.id ?? conversation.channelId,
          body,
          revision: 1,
          contextVersion: data.context.version,
          riskLevel: "normal",
          status: "draft",
          source,
          updatedAt: new Date().toISOString(),
        };
        setDraft(previewDraft);
        setDraftBody(previewDraft.body);
        setMessage("Preview draft created locally. It cannot be sent externally.");
      } else if (accessToken) {
        const created = await apiRequest<DraftRevision>("/api/facebook/drafts", accessToken, {
          method: "POST",
          body: JSON.stringify(source === "human" ? { conversationId: conversation.id, body: humanBody } : { conversationId: conversation.id }),
        });
        if (selectedConversationRef.current !== requestedConversationId || created.targetId !== requestedConversationId || created.channelId !== conversation.channelId) return;
        setDraft(created);
        setDraftBody(created.body);
      }
    } catch (reason) {
      setError(errorText(reason));
    } finally {
      setBusy(null);
    }
  }

  async function approveDraft() {
    if (!draft || !draftBody.trim() || !conversation || !draftMatchesConversation) return;
    setBusy("approve");
    setMessage(null);
    setError(null);
    const requestedConversationId = conversation.id;
    try {
      if (status === "preview") {
        setDraft({ ...draft, body: draftBody, status: "approved" });
        setMessage("Preview approval recorded locally. Live send remains unavailable.");
      } else if (accessToken) {
        const approved = await apiRequest<DraftRevision>(`/api/facebook/drafts/${encodeURIComponent(draft.id)}/approve`, accessToken, {
          method: "POST",
          body: JSON.stringify({ body: draftBody, revision: draft.revision }),
        });
        if (selectedConversationRef.current !== requestedConversationId || approved.targetId !== requestedConversationId || approved.channelId !== conversation.channelId) return;
        setDraft(approved);
        setDraftBody(approved.body);
        setMessage(`Revision ${approved.revision} approved. Review the exact approved text below before sending.`);
      }
    } catch (reason) {
      setError(errorText(reason));
    } finally {
      setBusy(null);
    }
  }

  async function sendDraft() {
    if (!draft || !conversation || !draftMatchesConversation || draft.status !== "approved" || draftBody !== draft.body || !withinWindow || !accessToken) return;
    setBusy("send");
    setMessage(null);
    setError(null);
    try {
      const result = await apiRequest<FacebookSendResult>(`/api/facebook/drafts/${encodeURIComponent(draft.id)}/send`, accessToken, {
        method: "POST",
        body: JSON.stringify({ revision: draft.revision, body: draft.body, conversationId: conversation.id }),
      });
      await loadConversation(conversation.id);
      setMessage(result.status === "sent"
        ? "Meta confirmed that the approved reply was sent."
        : `Send result: ${result.status}. Check the operation before treating this reply as sent.`);
    } catch (reason) {
      setError(errorText(reason));
    } finally {
      setBusy(null);
    }
  }

  const approvedExactText = draft?.status === "approved" && draftBody === draft.body;
  const sendDisabled = status === "preview"
    || !approvedExactText
    || !draftMatchesConversation
    || !withinWindow
    || data.settings.sendsPaused
    || channel?.status !== "connected"
    || busy !== null;
  const sendLabel = status === "preview"
    ? "Send unavailable in preview"
    : !withinWindow
      ? "Send unavailable outside 24h"
      : data.settings.sendsPaused
        ? "All sends paused"
        : channel?.status !== "connected"
          ? "Facebook Page is not connected"
          : !approvedExactText
            ? "Approve exact reply before sending"
            : busy === "send" ? "Sending…" : "Send approved reply";

  return (
    <>
      <PageHeader title="Inbox" description="Read the conversation, shape the draft, approve the exact revision, then send with a separate final action." />
      {error ? <p className="form-error" role="alert">{error}</p> : null}
      {loading ? <p className="panel-notice" role="status">Loading Facebook inbox…</p> : null}
      <div className="triage-layout">
        <aside className="conversation-list" aria-label="Conversations">
          <div className="list-toolbar"><strong>Messages</strong><Badge tone="blue">{inbox.filter((item) => item.unreadCount > 0).length} new</Badge></div>
          {inbox.map((item) => (
            <button className={`conversation-row ${item.id === effectiveSelectedId ? "active" : ""}`} type="button" key={item.id} onClick={() => { selectedConversationRef.current = item.id; setSelectedId(item.id); }} disabled={busy !== null}>
              <span className="mini-avatar">{item.participantLabel.slice(0, 1).toUpperCase()}</span>
              <span><span><strong>{item.participantLabel}</strong><time>{formatRelativeTime(item.updatedAt)}</time></span><small lang="my">{item.preview}</small><Badge tone={item.reviewStatus === "needs_review" ? "amber" : "neutral"}>{item.reviewStatus.replaceAll("_", " ")}</Badge></span>
            </button>
          ))}
          {status === "authenticated" && facebookInbox?.hasMore ? (
            <button className="button button--secondary button--full" type="button" disabled={loadingMore || busy !== null} onClick={() => { if (facebookInbox.cursor) void loadFacebookInbox(facebookInbox.cursor); }}>
              {loadingMore ? "Loading…" : "Load more conversations"}
            </button>
          ) : null}
          {!loading && inbox.length === 0 ? <EmptyState title="No Facebook conversations" body="Connect a Page and sync messages to load its inbox." /> : null}
        </aside>

        <section className="conversation-panel">
          {detailLoading && status === "authenticated" ? <p className="panel-notice" role="status">Loading selected conversation…</p> : conversation ? (
            <>
              <header><div><span className="mini-avatar mini-avatar--large">{conversation.participantLabel.slice(0, 1).toUpperCase()}</span><span><strong>{conversation.participantLabel}</strong><small>{channel?.name}</small></span></div><Badge tone={withinWindow ? "green" : "red"}><Clock3 size={13} />{withinWindow ? "Within 24h" : "Window closed"}</Badge></header>
              <div className="message-thread">
                {conversation.messages.map((item) => <div className={`message-bubble message-bubble--${item.direction}`} key={item.id}><p lang="my">{item.body}</p><time>{formatRelativeTime(item.sentAt)}</time></div>)}
              </div>
            </>
          ) : <EmptyState title="Choose a conversation" body="Select a synced Facebook message to start a reviewed reply." />}
        </section>

        <aside className="draft-panel">
          <header><div><Sparkles size={18} /><strong>Draft review</strong></div><Badge tone="blue">Context v{data.context.version}</Badge></header>
          {conversation ? (
            <div className="draft-panel__body">
              <div className="context-facts"><span>Using</span>{data.context.products.filter((product) => channel?.productIds.includes(product.id)).map((product) => <Badge key={product.id}>{product.name}</Badge>)}</div>
              <label className="field field--grow"><span>Reply</span><textarea aria-label="Reply" lang="my" rows={11} value={draftBody} onChange={(event) => setDraftBody(event.target.value)} placeholder="Generate a context-aware draft or write your own reply…" /></label>
              {draft?.riskLevel === "sensitive" || draft?.riskLevel === "unknown" ? <p className="form-error">This reply requires human review.</p> : null}
              {draft?.status === "approved" ? <p className="panel-notice"><strong>Approved revision {draft.revision}</strong><br /><span lang="my">{draft.body}</span>{draftBody !== draft.body ? <><br />Edited text needs approval again.</> : null}</p> : null}
              {operation ? <p className={operation.status === "needs_verification" || operation.status === "failed" ? "form-error" : "panel-notice"} role="status"><strong>Send operation: {operation.status.replaceAll("_", " ")}</strong>{operation.providerMessageId ? <><br />Meta message ID: {operation.providerMessageId}</> : null}{operation.status === "needs_verification" ? <><br />Delivery is uncertain. Verify in Meta before creating another reply.</> : null}</p> : null}
              {message ? <p className="panel-notice" role="status">{message}</p> : null}
              <div className="draft-actions">
                <button className="button button--secondary" type="button" onClick={() => void createDraft("ai")} disabled={busy !== null}><Sparkles size={16} />{busy === "generate" ? "Drafting…" : "AI draft"}</button>
                <button className="button button--secondary" type="button" onClick={() => void createDraft("human")} disabled={!draftBody.trim() || busy !== null}><PenLine size={16} />{busy === "human" ? "Saving…" : "Use written reply"}</button>
                <button className="button button--primary button--full" type="button" onClick={() => void approveDraft()} disabled={!draft || !draftBody.trim() || !draftMatchesConversation || busy !== null}><Check size={16} />{busy === "approve" ? "Approving…" : "Approve exact text"}</button>
                <button className="button button--dark button--full" type="button" onClick={() => void sendDraft()} disabled={sendDisabled}><Send size={16} />{sendLabel}</button>
              </div>
              <p className="approval-note">Editing approved text disables send until that exact body and revision are approved again. AutoSocial never sends automatically from this screen.</p>
            </div>
          ) : null}
        </aside>
      </div>
    </>
  );
}

function errorText(reason: unknown): string {
  return reason instanceof Error ? reason.message : "AutoSocial could not complete the Facebook request.";
}
